// Enhanced camera utility functions for evidence capture

export interface CameraOptions {
  width?: number;
  height?: number;
  facingMode?: 'user' | 'environment';
  quality?: number;
  aspectRatio?: number | 'cover';
  enableTimer?: boolean;
  timerDuration?: number;
  enableFlash?: boolean;
}

export interface TimerOptions {
  duration: number;
  onTick?: (remaining: number) => void;
  onComplete?: () => void;
}

export interface CapturedPhoto {
  blob: Blob;
  dataUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  metadata?: {
    timestamp: number;
    location?: {
      lat: number;
      lng: number;
    };
    flashUsed?: boolean;
    timerUsed?: boolean;
  };
}

export interface AudioRecording {
  blob: Blob;
  dataUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  duration: number;
  metadata?: {
    timestamp: number;
    location?: {
      lat: number;
      lng: number;
    };
  };
}

export class CameraError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'CameraError';
  }
}

// ✅ NEW: Camera state machine types
export type CameraState = 'idle' | 'requesting' | 'active' | 'failed' | 'cleanup';

// ✅ NEW: Global camera state manager
class CameraStateManager {
  private static instance: CameraStateManager;
  private state: CameraState = 'idle';
  private stateChangeListeners: Array<(state: CameraState) => void> = [];
  private lastStateChange: number = 0;

  private constructor() {}

  static getInstance(): CameraStateManager {
    if (!CameraStateManager.instance) {
      CameraStateManager.instance = new CameraStateManager();
    }
    return CameraStateManager.instance;
  }

  getState(): CameraState {
    return this.state;
  }

  setState(newState: CameraState): void {
    if (this.state === newState) return;

    const oldState = this.state;
    this.state = newState;
    this.lastStateChange = Date.now();

    console.log(`📷 Camera state: ${oldState} → ${newState}`);

    // Notify listeners
    this.stateChangeListeners.forEach(listener => listener(newState));
  }

  onStateChange(listener: (state: CameraState) => void): void {
    this.stateChangeListeners.push(listener);
  }

  getLastStateChange(): number {
    return this.lastStateChange;
  }

  canRequestCamera(): boolean {
    return this.state === 'idle';
  }

  isCameraActive(): boolean {
    return this.state === 'active';
  }

  isCleanupRequired(): boolean {
    return this.state === 'active' || this.state === 'requesting';
  }
}

export class CameraService {
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private currentFacingMode: 'user' | 'environment' = 'environment';
  private flashSupported = false;
  private initializationPromise: Promise<MediaStream> | null = null;
  private isInitialized = false;
  private lastUsedOptions: CameraOptions = {};
  // ✅ NEW: Camera state manager integration
  private stateManager: CameraStateManager = CameraStateManager.getInstance();

  /**
   * ✅ ENHANCED: Request camera permissions and check availability with enhanced browser compatibility
   */
  async checkCameraSupport(): Promise<boolean> {
    // Check basic MediaDevices support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('MediaDevices API not supported in this browser');
      return false;
    }

    // Check MediaRecorder support for audio recording
    if (typeof MediaRecorder === 'undefined') {
      console.warn('MediaRecorder API not supported in this browser');
      return false;
    }

    // ✅ FIXED: Enhanced HTTPS check for Telegram WebView compatibility
    const isTelegram = typeof window !== 'undefined' && !!window.Telegram?.WebApp;
    const isSecureContext = location.protocol === 'https:' || location.hostname === 'localhost' || isTelegram;

    if (!isSecureContext) {
      console.warn('HTTPS required for camera access on this domain');
      return false;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(device => device.kind === 'videoinput');
      const hasAudioInput = devices.some(device => device.kind === 'audioinput');

      console.log('Device capabilities:', {
        hasVideoInput,
        hasAudioInput,
        isTelegram,
        isSecureContext
      });
      return hasVideoInput && hasAudioInput;
    } catch (error) {
      console.error('Error checking camera support:', error);
      return false;
    }
  }

  /**
   * ✅ ENHANCED: Initialize camera with proper state machine and timeout protection
   */
  async initializeCamera(options: CameraOptions = {}): Promise<MediaStream> {
    // ✅ NEW: Check if camera can be requested based on state
    if (!this.stateManager.canRequestCamera()) {
      const currentState = this.stateManager.getState();
      console.warn(`⚠️ Camera cannot be requested in current state: ${currentState}`);

      if (currentState === 'active') {
        // If camera is already active, check if we can reuse the stream
        const optionsChanged = this.haveOptionsChanged(options);
        if (!optionsChanged && this.stream) {
          console.log('♻️ Camera already active with same options, reusing stream');
          return this.stream;
        } else if (optionsChanged) {
          console.log('🔄 Options changed, stopping previous stream');
          await this.stopCamera();
        }
      } else if (currentState === 'requesting') {
        // Wait for current request to complete
        console.log('⏳ Camera initialization already in progress, waiting...');
        if (this.initializationPromise) {
          return this.initializationPromise;
        }
      }
    }

    // Return existing promise if initialization is in progress
    if (this.initializationPromise) {
      console.log('⏳ Camera initialization already in progress, returning existing promise');
      return this.initializationPromise;
    }

    // ✅ FIXED: Reuse existing stream if already initialized with same options
    // Check if stream is still active and not ended
    if (this.isInitialized && this.stream) {
      const tracks = this.stream.getTracks();
      const hasActiveTracks = tracks.some(track => track.readyState === 'live' && !track.ended);

      if (hasActiveTracks) {
        const optionsChanged = this.haveOptionsChanged(options);
        if (!optionsChanged) {
          console.log('♻️ Reusing existing camera stream with same options');
          return this.stream;
        } else {
          console.log('🔄 Options changed, stopping previous stream');
          await this.stopCamera();
        }
      } else {
        console.log('⚠️ Previous stream ended, cleaning up and reinitializing');
        await this.stopCamera();
      }
    }

    // ✅ NEW: Set state to requesting
    this.stateManager.setState('requesting');

    // Set initialization promise to prevent multiple concurrent calls
    this.initializationPromise = this.doInitializeCamera(options);

    try {
      const stream = await this.initializationPromise;
      this.isInitialized = true;
      this.lastUsedOptions = { ...options };

      // ✅ NEW: Set state to active on success
      this.stateManager.setState('active');

      return stream;
    } catch (error) {
      // ✅ NEW: Set state to failed on error
      this.stateManager.setState('failed');
      throw error;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * ✅ ENHANCED: Internal camera initialization with Telegram-specific constraints and timeout
   */
  private async doInitializeCamera(options: CameraOptions = {}): Promise<MediaStream> {
    this.currentFacingMode = options.facingMode || 'user'; // Default to front camera for selfie

    // ✅ ENHANCED: Telegram-specific constraints for better compatibility
    const isTelegram = typeof window !== 'undefined' && !!window.Telegram?.WebApp;
    const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    // Optimized constraints for faster initialization with Telegram compatibility
    const constraints: MediaStreamConstraints = {
      video: {
        // ✅ FIXED: Use more flexible constraints for better Telegram WebView compatibility
        width: isIOS ? { ideal: options.width || 720 } : { ideal: options.width || 640 },
        height: isIOS ? { ideal: options.height || 1280 } : { ideal: options.height || 480 },
        facingMode: this.currentFacingMode,
        // ✅ ENHANCED: Add platform-specific optimizations
        ...(isTelegram && isAndroid ? {
          // Android Telegram WebView prefers these settings
          frameRate: { ideal: 30, max: 30 },
          aspectRatio: { ideal: 16/9 }
        } : {}),
        ...(isIOS ? {
          // iOS prefers simpler constraints
          facingMode: { ideal: this.currentFacingMode }
        } : {})
      },
      audio: false
    };

    try {
      console.log('🚀 Starting camera initialization with Telegram-optimized constraints...', {
        isTelegram,
        isIOS,
        isAndroid,
        constraints
      });
      const startTime = Date.now();

      // ✅ ENHANCED: Add timeout with better error recovery
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new CameraError('Camera initialization timed out after 10 seconds. Please ensure camera permissions are granted.', 'TimeoutError'));
          }, 10000); // 10 second timeout
        })
      ]);

      this.stream = stream;

      const initializationTime = Date.now() - startTime;
      console.log(`✅ Camera initialized in ${initializationTime}ms`);

      // Check flash support asynchronously without blocking
      this.checkFlashSupportAsync();

      return this.stream;
    } catch (error: unknown) {
      const err = error as Error;
      console.error('❌ Camera initialization failed:', err);

      // Track the most informative error to report if the fallback also fails
      let reportError: unknown = error;

      // ✅ ENHANCED: Better error recovery for Telegram WebView
      if (isTelegram) {
        console.warn('⚠️ Camera initialization failed in Telegram WebView, attempting fallback...');
        try {
          // Fallback to simpler constraints
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 640 },
              height: { ideal: 480 },
              facingMode: this.currentFacingMode
            },
            audio: false
          });
          this.stream = fallbackStream;
          console.log('✅ Camera initialized with fallback constraints');
          return this.stream;
        } catch (fallbackError) {
          console.error('❌ Camera fallback also failed:', fallbackError);
          // ✅ FIXED: Prefer the fallback error when it pinpoints a hardware-level
          // problem (camera in use / unsupported constraints) so the user gets a
          // device-specific message instead of a generic one.
          const fallbackName = (fallbackError as Error)?.name;
          if (fallbackName === 'NotReadableError' || fallbackName === 'OverconstrainedError') {
            reportError = fallbackError;
          }
        }
      }

      throw new CameraError(
        this.getCameraErrorMessage(reportError),
        (reportError as Error).name
      );
    }
  }

  /**
   * Check if options have changed to determine if reinitialization is needed
   */
  private haveOptionsChanged(newOptions: CameraOptions): boolean {
    return (
      this.lastUsedOptions.facingMode !== newOptions.facingMode ||
      this.lastUsedOptions.width !== newOptions.width ||
      this.lastUsedOptions.height !== newOptions.height
    );
  }

  /**
   * Check if flash is supported on current camera
   */
  private async checkFlashSupport(): Promise<void> {
    if (!this.stream) return;
    
    try {
      const track = this.stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      this.flashSupported = 'torch' in capabilities;
    } catch {
      this.flashSupported = false;
    }
  }

  /**
   * Asynchronous flash support check without blocking initialization
   */
  private checkFlashSupportAsync(): void {
    if (!this.stream) return;
    
    // Run flash support check asynchronously to avoid blocking
    setTimeout(async () => {
      try {
        const track = this.stream?.getVideoTracks()[0];
        if (track) {
          const capabilities = track.getCapabilities();
          this.flashSupported = 'torch' in capabilities;
          console.log('🔦 Flash support checked asynchronously:', this.flashSupported);
        }
      } catch (error) {
        this.flashSupported = false;
        console.log('🔦 Flash support check failed:', error);
      }
    }, 100); // Small delay to not block camera initialization
  }

  /**
   * Start camera preview on video element
   */
  startPreview(videoElement: HTMLVideoElement, onError?: (error: Error) => void): void {
    if (!this.stream) {
      throw new CameraError('Camera not initialized. Call initializeCamera first.');
    }

    this.video = videoElement;
    this.video.srcObject = this.stream;
    
    // Handle play() promise properly to avoid AbortError
    const playPromise = this.video.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Auto-play was prevented or interrupted
        console.warn('Video play interrupted:', error);
        
        // Try to play again after a short delay
        setTimeout(() => {
          if (this.video && this.video.srcObject) {
            this.video.play().catch(retryError => {
              console.warn('Video play retry failed:', retryError);
              // ✅ FIXED: Surface the failure instead of silently leaving a black preview
              const playErrName = (retryError as Error)?.name || 'PreviewError';
              const playErrMessage = retryError instanceof Error ? retryError.message : String(retryError);
              onError?.(new CameraError(
                `Unable to start camera preview: ${playErrMessage}`,
                playErrName
              ));
            });
          } else {
            // Stream was torn down before the retry could run
            onError?.(new CameraError('Camera preview could not start: stream is no longer available.', 'PreviewError'));
          }
        }, 100);
      });
    }
  }

  /**
   * Enhanced photo capture with timer and flash support
   */
  async capturePhoto(options: CameraOptions = {}): Promise<CapturedPhoto> {
    if (!this.video || !this.stream) {
      throw new CameraError('Camera not initialized or preview not started.');
    }

    // Handle timer if enabled
    if (options.enableTimer && options.timerDuration) {
      await this.executeTimer(options.timerDuration);
    }

    // Handle flash if enabled
    let flashWasUsed = false;
    if (options.enableFlash && this.flashSupported) {
      flashWasUsed = await this.toggleFlash(true);
    }

    try {
      // Create canvas to capture frame
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        throw new CameraError('Unable to create canvas context for photo capture.');
      }

      // Set canvas dimensions
      canvas.width = this.video.videoWidth;
      canvas.height = this.video.videoHeight;

      // Draw current video frame to canvas
      context.drawImage(this.video, 0, 0);

      // Get location if available
      const location = await this.getCurrentLocation().catch(() => undefined);

      // Convert to blob
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new CameraError('Failed to create photo blob.'));
              return;
            }

            const dataUrl = canvas.toDataURL('image/jpeg', options.quality || 0.8);
            const fileName = `evidence_${Date.now()}.jpg`;

            resolve({
              blob,
              dataUrl,
              fileName,
              fileSize: blob.size,
              mimeType: blob.type,
              metadata: {
                timestamp: Date.now(),
                location,
                flashUsed: flashWasUsed,
                timerUsed: options.enableTimer || false,
              },
            });
          },
          'image/jpeg',
          options.quality || 0.8
        );
      });
    } finally {
      // Turn off flash if it was used
      if (flashWasUsed) {
        await this.toggleFlash(false);
      }
    }
  }

  /**
   * Execute timer countdown
   */
  private async executeTimer(duration: number): Promise<void> {
    return new Promise((resolve) => {
      let remaining = duration;
      
      const countdown = () => {
        if (remaining <= 0) {
          resolve();
          return;
        }
        
        // Emit timer tick event (can be listened to for UI updates)
        window.dispatchEvent(
          new CustomEvent('cameraTimer', { detail: { remaining } })
        );
        
        remaining--;
        setTimeout(countdown, 1000);
      };
      
      countdown();
    });
  }

  /**
   * Toggle camera flash/torch
   */
  private async toggleFlash(enable: boolean): Promise<boolean> {
    if (!this.stream || !this.flashSupported) return false;
    
    try {
      const track = this.stream.getVideoTracks()[0];
      await track.applyConstraints({
        advanced: [{ torch: enable } as {torch: boolean}]
      });
      return true;
    } catch (error) {
      console.warn('Flash control failed:', error);
      return false;
    }
  }

  /**
   * Get current geolocation
   */
  private async getCurrentLocation(): Promise<{ lat: number; lng: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 30000,
        }
      );
    });
  }

  /**
   * Switch between front and back camera
   */
  async switchCamera(): Promise<MediaStream> {
    const newFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    
    this.stopCamera();
    return this.initializeCamera({ facingMode: newFacingMode });
  }

  /**
   * Start audio recording
   */
  async startAudioRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      });
      
      this.recordedChunks = [];
      
      // Try different formats for better compatibility
      let options: MediaRecorderOptions;
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options = { mimeType: 'audio/webm;codecs=opus' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
        options = { mimeType: 'audio/mpeg' };
      } else {
        options = {}; // Use browser default
      }
      
      this.mediaRecorder = new MediaRecorder(stream, options);
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.start(100); // Collect data every 100ms
    } catch (error) {
      throw new CameraError('Failed to start audio recording: ' + (error as Error).message);
    }
  }

  /**
   * Stop audio recording and return audio data
   */
  async stopAudioRecording(): Promise<AudioRecording> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new CameraError('No active audio recording'));
        return;
      }
      
      const startTime = Date.now();
      
      this.mediaRecorder.onstop = async () => {
        try {
          // Use the same MIME type that was used for recording
          const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
          const blob = new Blob(this.recordedChunks, { type: mimeType });
          const dataUrl = await this.blobToDataUrl(blob);
          
          // Generate appropriate file extension
          const extension = mimeType.includes('webm') ? 'webm' : 
                           mimeType.includes('mp4') ? 'mp4' : 
                           mimeType.includes('mpeg') ? 'mp3' : 'webm';
          const fileName = `audio_${Date.now()}.${extension}`;
          const duration = Date.now() - startTime;
          const location = await this.getCurrentLocation().catch(() => undefined);
          
          // Stop all tracks
          if (this.mediaRecorder?.stream) {
            this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
          }
          
          resolve({
            blob,
            dataUrl,
            fileName,
            fileSize: blob.size,
            mimeType: blob.type,
            duration,
            metadata: {
              timestamp: Date.now(),
              location,
            },
          });
        } catch (error) {
          reject(new CameraError('Failed to process audio recording: ' + (error as Error).message));
        }
      };
      
      this.mediaRecorder.stop();
    });
  }

  /**
   * Convert blob to data URL
   */
  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get current camera facing mode
   */
  getCurrentFacingMode(): 'user' | 'environment' {
    return this.currentFacingMode;
  }

  /**
   * Check if flash is supported
   */
  isFlashSupported(): boolean {
    return this.flashSupported;
  }

  /**
   * Check if camera is already initialized and stream is active
   */
  isCameraInitialized(): boolean {
    return this.isInitialized && 
           this.stream && 
           !this.stream.getTracks().some(track => track.readyState === 'ended' || track.muted);
  }

  /**
   * Get current stream without reinitializing (if already active)
   */
  getCurrentStream(): MediaStream | null {
    if (this.isCameraInitialized()) {
      return this.stream;
    }
    return null;
  }

  /**
   * Reset initialization cache (useful for troubleshooting)
   */
  resetInitializationCache(): void {
    this.isInitialized = false;
    this.initializationPromise = null;
    this.lastUsedOptions = {};
    console.log('🔄 Camera initialization cache reset');
  }

  /**
   * Check if audio recording is active
   */
  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  /**
   * ✅ ENHANCED: Ultimate camera stream cleanup with comprehensive timeout protection and state management
   * Prevents camera lock issues through multi-layered cleanup strategy with verification
   */
  async stopCamera(): Promise<void> {
    const cleanup = async (resolve: () => void, reject: (error: unknown) => void) => {
      const cleanupStartTime = Date.now();
      const TIMEOUT_TOTAL = 8000; // 8 second total timeout
      const TIMEOUT_TRACK = 3000; // 3 second per track timeout
      const TIMEOUT_VERIFY = 2000; // 2 second verification timeout

      console.log('🛑 Starting enhanced camera cleanup with comprehensive protection...');

      // ✅ NEW: Set state to cleanup
      this.stateManager.setState('cleanup');

      try {
        // ⚠️ CRITICAL: Prevent double cleanup
        if (!this.stream && !this.mediaRecorder && !this.isInitialized) {
          console.log('✅ Camera already clean, skipping cleanup');
          // ✅ NEW: Set state back to idle
          this.stateManager.setState('idle');
          resolve();
          return;
        }

        // Create overall timeout protection
        const cleanupTimeout = setTimeout(() => {
          console.warn('⏱️ Cleanup timeout reached, forcing reset');
          this.forceResetCamera();
          // ✅ NEW: Set state back to idle on timeout
          this.stateManager.setState('idle');
          resolve();
        }, TIMEOUT_TOTAL);

        // PHASE 1: Stream cleanup with enhanced verification
        if (this.stream) {
          const tracks = this.stream.getTracks();
          console.log(`📹 Found ${tracks.length} tracks to clean up`);

          const trackCleanupPromises = tracks.map((track, index) => {
            return new Promise<void>((trackResolve) => {
              const trackTimeout = setTimeout(() => {
                console.warn(`⏱️ Track ${index} cleanup timeout, forcing stop`);
                try {
                  track.stop();
                } catch {
                  // Track might already be stopped
                }
                trackResolve();
              }, TIMEOUT_TRACK);

              try {
                // Stop the track immediately
                track.stop();

                // Enhanced verification loop
                const verifyTrackStopped = (attempts = 0) => {
                  if (attempts > 20) { // Max 20 attempts (1 second)
                    console.warn(`⚠️ Track ${index} verification timeout, assuming stopped`);
                    clearTimeout(trackTimeout);
                    trackResolve();
                    return;
                  }

                  if (track.readyState === 'ended') {
                    console.log(`✅ Track ${index} verified stopped`);
                    clearTimeout(trackTimeout);
                    trackResolve();
                  } else {
                    setTimeout(() => verifyTrackStopped(attempts + 1), 50);
                  }
                };

                // Start verification after brief delay
                setTimeout(() => verifyTrackStopped(), 10);

              } catch (error) {
                console.warn(`⚠️ Error stopping track ${index}:`, error);
                clearTimeout(trackTimeout);
                trackResolve(); // Continue cleanup even if this track fails
              }
            });
          });

          // Wait for all tracks with timeout
          await Promise.race([
            Promise.all(trackCleanupPromises),
            new Promise(resolve => setTimeout(resolve, TIMEOUT_TRACK * tracks.length))
          ]);

          console.log('✅ All stream tracks cleaned up');
        }

        // PHASE 2: Media recorder cleanup
        if (this.mediaRecorder) {
          console.log('🎤 Cleaning up media recorder...');

          if (this.mediaRecorder.state === 'recording') {
            try {
              this.mediaRecorder.stop();

              // Wait for recorder to actually stop
              await new Promise<void>((recorderResolve) => {
                const checkStopped = (attempts = 0) => {
                  if (attempts > 40) { // Max 40 attempts (2 seconds)
                    console.warn('⚠️ Media recorder verification timeout');
                    recorderResolve();
                    return;
                  }

                  if (this.mediaRecorder?.state === 'inactive') {
                    console.log('✅ Media recorder verified stopped');
                    recorderResolve();
                  } else {
                    setTimeout(() => checkStopped(attempts + 1), 50);
                  }
                };

                setTimeout(() => checkStopped(), 10);
              });

            } catch (error) {
              console.warn('⚠️ Error stopping media recorder:', error);
            }
          }

          // Clean up recorder stream tracks
          if (this.mediaRecorder.stream) {
            const audioTracks = this.mediaRecorder.stream.getTracks();
            console.log(`🎤 Cleaning up ${audioTracks.length} audio tracks from recorder`);
            audioTracks.forEach(track => {
              try {
                track.stop();
              } catch (e) {
                console.warn('⚠️ Error stopping audio track:', e);
              }
            });
          }
        }

        // PHASE 3: Final verification with timeout protection
        console.log('🔍 Performing final verification...');

        const verificationResults = {
          streamCleared: false,
          recorderCleared: false,
          videoCleared: false,
          activeTracksFound: 0
        };

        await Promise.race([
          new Promise<void>((verifyResolve) => {
            // Check stream
            if (this.stream) {
              const activeTracks = this.stream.getTracks().filter(track => track.readyState === 'live');
              verificationResults.activeTracksFound = activeTracks.length;

              if (activeTracks.length > 0) {
                console.warn(`⚠️ Found ${activeTracks.length} active tracks, forcing cleanup`);
                activeTracks.forEach(track => {
                  try {
                    track.stop();
                  } catch (e) {
                    console.error('Error force-stopping track:', e);
                  }
                });
              } else {
                verificationResults.streamCleared = true;
              }
            } else {
              verificationResults.streamCleared = true;
            }

            // Check recorder
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
              verificationResults.recorderCleared = true;
            }

            // Check video
            if (!this.video || !this.video.srcObject) {
              verificationResults.videoCleared = true;
            }

            verifyResolve();
          }),
          new Promise(resolve => setTimeout(resolve, TIMEOUT_VERIFY))
        ]);

        // PHASE 4: State reset
        console.log('🔄 Resetting camera state...');

        this.stream = null;
        this.isInitialized = false;
        this.initializationPromise = null;
        this.lastUsedOptions = {};
        this.mediaRecorder = null;
        this.recordedChunks = [];

        // Clean up video element
        if (this.video) {
          try {
            // Stop any ongoing video play
            if (!this.video.paused) {
              this.video.pause();
            }

            // Clear srcObject
            this.video.srcObject = null;

            // Load empty stream to release resources
            this.video.load();

          } catch (e) {
            console.warn('⚠️ Error clearing video element:', e);
          }
          this.video = null;
        }

        // Clear overall timeout
        clearTimeout(cleanupTimeout);

        const cleanupDuration = Date.now() - cleanupStartTime;
        console.log('✅ Enhanced camera cleanup completed successfully', {
          duration: `${cleanupDuration}ms`,
          verificationResults,
          hasStream: !!this.stream,
          hasMediaRecorder: !!this.mediaRecorder,
          hasVideo: !!this.video
        });

        // ✅ NEW: Set state back to idle after successful cleanup
        this.stateManager.setState('idle');

        resolve();

      } catch (error) {
        console.error('❌ Camera cleanup failed:', error);
        // Force reset even on error to prevent stuck state
        this.forceResetCamera();
        // ✅ NEW: State will be set to idle in forceResetCamera
        reject(error);
      }
    };
    return new Promise<void>((resolve, reject) => {
      void cleanup(resolve, reject).catch(reject);
    });
  }

  /**
   * ✅ NEW: Force reset camera as emergency cleanup
   */
  private forceResetCamera(): void {
    console.warn('🚨 Force resetting camera due to cleanup failure');
    try {
      if (this.stream) {
        this.stream.getTracks().forEach(track => {
          try {
            track.stop();
          } catch {
            // Ignore errors during force reset
          }
        });
      }

      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        try {
          this.mediaRecorder.stop();
        } catch {
          // Ignore errors during force reset
        }
      }

      if (this.video) {
        try {
          this.video.srcObject = null;
        } catch {
          // Ignore errors during force reset
        }
      }
    } catch {
      // Ignore all errors during force reset
    } finally {
      // Reset all references
      this.stream = null;
      this.isInitialized = false;
      this.initializationPromise = null;
      this.lastUsedOptions = {};
      this.mediaRecorder = null;
      this.recordedChunks = [];
      this.video = null;

      // ✅ NEW: Set state back to idle after force reset
      this.stateManager.setState('idle');
    }
  }

  /**
   * Get user-friendly error message from camera error
   */
  private getCameraErrorMessage(error: unknown): string {
    const err = error as Error;
    switch (err.name) {
      case 'NotAllowedError':
        return 'Camera access denied. Please allow camera permissions and try again.';
      case 'NotFoundError':
        return 'No camera found on this device.';
      case 'NotSupportedError':
        return 'Camera not supported by this browser.';
      case 'NotReadableError':
        return 'Camera is being used by another application.';
      case 'OverconstrainedError':
        return 'Camera settings not supported by this device.';
      case 'SecurityError':
        return 'Camera access blocked due to security settings.';
      default:
        return `Camera error: ${err.message || 'Unknown error occurred'}`;
    }
  }

  /**
   * Get available camera devices
   */
  async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(device => device.kind === 'videoinput');
    } catch (error) {
      console.error('Error getting camera devices:', error);
      return [];
    }
  }
}

// Create singleton instance
export const cameraService = new CameraService();

// ✅ NEW: Global cleanup on page unload to prevent camera locks
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    console.log('🧹 Cleaning up camera on page unload');
    cameraService.stopCamera().catch(err => {
      console.error('Error during cleanup on unload:', err);
    });
  });

  // ✅ NEW: Cleanup on page visibility change (Telegram WebView backgrounding)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('📱 App hidden (Telegram WebView backgrounded), cleaning up camera');
      cameraService.stopCamera().catch(err => {
        console.error('Error during cleanup on visibility change:', err);
      });
    }
  });
}

// Utility functions
export const checkCameraPermission = async (): Promise<PermissionState> => {
  if (!navigator.permissions) {
    throw new CameraError('Permissions API not supported');
  }

  try {
    const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return permission.state;
  } catch (error) {
    console.error('Error checking camera permission:', error);
    return 'prompt';
  }
};

export const requestCameraPermission = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    console.error('Camera permission denied:', error);
    return false;
  }
};

/**
 * Pick image from device gallery/local storage
 * Uses native file input with proper mobile optimizations
 */
/**
 * Accepted MIME types for image upload
 * Now includes HEIC/HEIF which will be converted
 */
const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/gif',
  'image/heic',
  'image/heif'
] as const;

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB limit

/**
 * Validate image file before processing
 */
function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check MIME type whitelist
  if (!ACCEPTED_IMAGE_MIME_TYPES.includes(file.type as any)) {
    return {
      valid: false,
      error: `Format ${file.type} tidak didukung. Gunakan: JPG, PNG, WEBP, BMP, GIF, HEIC, atau HEIF.`
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Ukuran file terlalu besar (${(file.size / 1024 / 1024).toFixed(1)}MB). Maksimum 15MB.`
    };
  }

  return { valid: true };
}

/**
 * Convert HEIC to JPEG using canvas (iOS Safari only)
 */
async function convertHeicToJpeg(file: File): Promise<{ blob: Blob; dataUrl: string }> {
  const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent);

  if (!isIOS) {
    throw new Error('HEIC only supported on iOS. Please use JPG/PNG/WEBP format.');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const heicDataUrl = e.target?.result as string;
      const img = new window.Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 1920;
        canvas.height = img.naturalHeight || 1080;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to create canvas for HEIC conversion'));
          return;
        }

        ctx.drawImage(img, 0, 0);
        const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.92);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, dataUrl: jpegDataUrl });
            } else {
              reject(new Error('HEIC conversion failed'));
            }
          },
          'image/jpeg',
          0.92
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load HEIC. Only supported on iOS Safari.'));
      };

      img.src = heicDataUrl;
    };
    reader.readAsDataURL(file);
  });
}

function isHeicFormat(file: File): boolean {
  return file.type.toLowerCase().includes('heic') || file.type.toLowerCase().includes('heif');
}

export const pickImageFromGallery = async (): Promise<CapturedPhoto | null> => {
  const openPicker = async (resolve: (photo: CapturedPhoto | null) => void, reject: (error: unknown) => void) => {
    // Create hidden file input element
    const input = document.createElement('input');
    input.type = 'file';
    // ✅ FIXED: Whitelist only supported image types
    input.accept = ACCEPTED_IMAGE_MIME_TYPES.join(',');
    input.multiple = false; // Single image selection

    // ✅ FIXED: Remove capture attribute to allow true gallery access on mobile
    // This prevents forcing camera mode on mobile devices which was causing the lock issue
    // input.setAttribute('capture', 'environment'); // ❌ REMOVED - This was forcing camera mode

    // Cleanup function to prevent memory leaks
    const cleanup = () => {
      input.removeEventListener('change', handleChange);
      input.removeEventListener('cancel', handleCancel);
      input.removeEventListener('error', handleError);
      if (document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    // Handle file selection
    const handleChange = async (event: Event) => {
      try {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file) {
          cleanup();
          resolve(null);
          return;
        }

        // ✅ FIXED: Use enhanced validation with MIME type whitelist
        const validation = validateImageFile(file);
        if (!validation.valid) {
          cleanup();
          // Show user-friendly error via Telegram popup if available
          if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
            (window as any).Telegram.WebApp.showAlert(validation.error || 'Invalid file');
          }
          reject(new CameraError(validation.error || 'Invalid file'));
          return;
        }

        // Handle HEIC conversion
        let blob: Blob;
        let dataUrl: string;
        let finalFileName = file.name;

        if (isHeicFormat(file)) {
          try {
            const converted = await convertHeicToJpeg(file);
            blob = converted.blob;
            dataUrl = converted.dataUrl;
            finalFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
          } catch {
            cleanup();
            const errorMsg = 'HEIC hanya didukung di iOS. Gunakan format JPG/PNG/WEBP.';
            if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
              (window as any).Telegram.WebApp.showAlert(errorMsg);
            }
            reject(new CameraError(errorMsg));
            return;
          }
        } else {
          blob = new Blob([file], { type: file.type });
          dataUrl = await new Promise<string>((resolveDataUrl, rejectDataUrl) => {
            const reader = new FileReader();
            reader.onload = () => resolveDataUrl(reader.result as string);
            reader.onerror = () => {
              rejectDataUrl(new CameraError('Failed to read image file'));
              reader.abort();
            };
            reader.readAsDataURL(blob);
          });
        }

        // Get location if available
        const location = await getCurrentLocationHelper().catch(() => undefined);

        // Return captured photo format
        const capturedPhoto: CapturedPhoto = {
          blob,
          dataUrl,
          fileName: finalFileName || `gallery_${Date.now()}.jpg`,
          fileSize: blob.size,
          mimeType: blob.type,
          metadata: {
            timestamp: Date.now(),
            location,
            flashUsed: false,
            timerUsed: false,
          },
        };

        cleanup();
        resolve(capturedPhoto);

      } catch (error) {
        cleanup();
        reject(new CameraError('Failed to process selected image: ' + (error as Error).message));
      }
    };

    // Handle cancel
    const handleCancel = () => {
      cleanup();
      resolve(null);
    };

    // Handle file picker error
    const handleError = () => {
      cleanup();
      reject(new CameraError('File picker encountered an error'));
    };

    // ✅ ENHANCED: Add comprehensive timeout protection with staggered timeouts
    const SHORT_TIMEOUT = 45000; // 45 seconds for initial picker open
    const LONG_TIMEOUT = 60000; // 60 seconds total operation timeout

    const shortTimeout = setTimeout(() => {
      console.warn('⏱️ Gallery picker failed to open within 45 seconds');
      cleanup();
      reject(new CameraError('Gallery picker timeout - file picker did not open. Please try again.'));
    }, SHORT_TIMEOUT);

    const longTimeout = setTimeout(() => {
      console.error('⏱️ Gallery picker operation exceeded 60 seconds total');
      cleanup();
      reject(new CameraError('Gallery picker operation timeout - processing took too long. Please try again.'));
    }, LONG_TIMEOUT);

    // Add event listeners with proper cleanup
    input.addEventListener('change', handleChange, { once: true });
    input.addEventListener('cancel', handleCancel, { once: true });
    input.addEventListener('error', handleError, { once: true });

    // Clear both timeouts on any completion
    const clearTimeouts = () => {
      clearTimeout(shortTimeout);
      clearTimeout(longTimeout);
    };

    input.addEventListener('change', () => clearTimeouts(), { once: true });
    input.addEventListener('cancel', () => clearTimeouts(), { once: true });
    input.addEventListener('error', () => clearTimeouts(), { once: true });

    // ✅ ENHANCED: Add input to DOM with proper timing to ensure it's ready
    input.style.display = 'none';
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    input.style.top = '-9999px';

    document.body.appendChild(input);

    // ✅ CRITICAL FIX: Add delay to ensure input is fully rendered and ready
    // This prevents issues on slow devices and Telegram WebView
    await new Promise(resolve => setTimeout(resolve, 100));

    // Verify input is in DOM before clicking
    if (!document.body.contains(input)) {
      cleanup();
      clearTimeouts();
      reject(new CameraError('Failed to add file input to DOM'));
      return;
    }

    // Additional safety check for mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('📱 Mobile device detected, adding extra delay for picker stability');
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // ✅ ENHANCED: Click with retry mechanism for flaky mobile browsers
    let clickAttempts = 0;
    const MAX_CLICK_ATTEMPTS = 3;

    const attemptClick = async (): Promise<boolean> => {
      clickAttempts++;

      try {
        input.click();
        console.log(`🔗 File picker click attempt ${clickAttempts}`);

        // Verify click was processed by checking if input has focus
        await new Promise(resolve => setTimeout(resolve, 50));

        return true;
      } catch (clickError) {
        console.warn(`⚠️ Click attempt ${clickAttempts} failed:`, clickError);

        if (clickAttempts < MAX_CLICK_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, 100));
          return attemptClick();
        } else {
          throw new CameraError(`Failed to trigger file picker after ${MAX_CLICK_ATTEMPTS} attempts`);
        }
      }
    };

    try {
      await attemptClick();
    } catch (retryError) {
      cleanup();
      clearTimeouts();
      reject(retryError);
      return;
    }
  };
  return new Promise<CapturedPhoto | null>((resolve, reject) => {
    void openPicker(resolve, reject).catch(reject);
  });
};

/**
 * Helper function to get current location
 * Extracted for reuse in gallery picker
 */
const getCurrentLocationHelper = async (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 30000,
      }
    );
  });
};